/**
 * LessonContent - Renders text and markdown content for lessons.
 * Uses react-markdown for safe markdown rendering.
 */

import { memo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '../../lib/utils'
import type { TextContent } from '../../curriculum/manager'
import type { OrderBook } from '../../engine/types'
import { ProbabilityDisplay } from '../visualizations/ProbabilityDisplay'
import { OrderBookChart } from '../visualizations/OrderBookChart'
import { PriceHistory } from '../visualizations/PriceHistory'

export interface LessonContentRendererProps {
  /** The text content to render */
  content: TextContent
  /** Current order book for visualizations */
  orderBook?: OrderBook
  /** Current price for probability display */
  currentPrice?: number | null
  /** Market question for context */
  marketQuestion?: string
  /** Visualization mode */
  visualizationMode?: 'beginner' | 'advanced'
  /** Optional className */
  className?: string
}

/**
 * Renders the appropriate visualization component based on visualizationId.
 */
function VisualizationRenderer({
  visualizationId,
  orderBook,
  currentPrice,
  marketQuestion,
  visualizationMode = 'beginner',
}: {
  visualizationId: string
  orderBook?: OrderBook
  currentPrice?: number | null
  marketQuestion?: string
  visualizationMode?: 'beginner' | 'advanced'
}) {
  switch (visualizationId) {
    case 'probability-display':
      return (
        <ProbabilityDisplay
          price={currentPrice ?? 0.5}
          question={marketQuestion ?? 'Will this event happen?'}
        />
      )

    case 'order-book':
      if (!orderBook) {
        return (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-center text-sm text-muted-foreground">
              Order book will appear when you start an interactive exercise.
            </p>
          </div>
        )
      }
      return (
        <OrderBookChart
          orderBook={orderBook}
          mode={visualizationMode}
        />
      )

    case 'price-history': {
      const trades = orderBook?.trades ?? []
      if (trades.length === 0) {
        return (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-center text-sm text-muted-foreground">
              Price history will appear after trades occur.
            </p>
          </div>
        )
      }
      return <PriceHistory trades={trades} height={150} />
    }

    default:
      return (
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-center text-sm text-muted-foreground">
            Unknown visualization: {visualizationId}
          </p>
        </div>
      )
  }
}

/**
 * LessonContentRenderer displays markdown content with optional visualization.
 */
export const LessonContentRenderer = memo(function LessonContentRenderer({
  content,
  orderBook,
  currentPrice,
  marketQuestion,
  visualizationMode = 'beginner',
  className,
}: LessonContentRendererProps) {
  const { markdown, visualizationId } = content

  return (
    <div className={cn('space-y-4', className)}>
      {/* Visualization */}
      {visualizationId && (
        <VisualizationRenderer
          visualizationId={visualizationId}
          orderBook={orderBook}
          currentPrice={currentPrice}
          marketQuestion={marketQuestion}
          visualizationMode={visualizationMode}
        />
      )}

      {/* Markdown content */}
      <div
        className={cn(
          'prose prose-sm max-w-none dark:prose-invert',
          // Headings
          'prose-headings:font-semibold prose-headings:text-foreground',
          'prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg',
          'prose-h1:mb-4 prose-h2:mb-3 prose-h3:mb-2',
          // Paragraphs
          'prose-p:text-foreground prose-p:leading-relaxed',
          // Lists
          'prose-ul:text-foreground prose-ol:text-foreground',
          'prose-li:marker:text-muted-foreground',
          // Links
          'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
          // Code
          'prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5',
          'prose-code:text-sm prose-code:font-mono prose-code:text-foreground',
          'prose-code:before:content-none prose-code:after:content-none',
          // Code blocks
          'prose-pre:bg-muted prose-pre:rounded-lg',
          // Blockquotes
          'prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground',
          // Strong/emphasis
          'prose-strong:text-foreground prose-em:text-foreground',
          // Tables
          'prose-table:text-sm',
          'prose-th:text-foreground prose-th:font-semibold',
          'prose-td:text-foreground'
        )}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Custom link handling - opens external links in new tab
            a: ({ href, children, ...props }) => {
              const isExternal = href?.startsWith('http')
              return (
                <a
                  href={href}
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  {...props}
                >
                  {children}
                </a>
              )
            },
            // Add beginner-friendly styling to certain terms
            strong: ({ children, ...props }) => {
              const text = String(children)
              // Highlight key trading terms
              const isKeyTerm = [
                'bid',
                'ask',
                'spread',
                'limit order',
                'market order',
                'probability',
                'share',
                'position',
              ].some((term) => text.toLowerCase().includes(term))

              return (
                <strong
                  className={cn(isKeyTerm && 'text-primary')}
                  {...props}
                >
                  {children}
                </strong>
              )
            },
          }}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    </div>
  )
})

/**
 * Beginner-friendly callout component for use in lessons.
 */
export interface CalloutProps {
  /** Type of callout */
  type: 'info' | 'tip' | 'warning' | 'example'
  /** Title text */
  title?: string
  /** Content */
  children: React.ReactNode
  /** Optional className */
  className?: string
}

const calloutStyles = {
  info: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
  tip: 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
  warning: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
  example: 'border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-400',
}

const calloutTitles = {
  info: 'Info',
  tip: 'Tip',
  warning: 'Note',
  example: 'Example',
}

export const Callout = memo(function Callout({
  type,
  title,
  children,
  className,
}: CalloutProps) {
  return (
    <div
      className={cn(
        'rounded-lg border-l-4 p-4',
        calloutStyles[type],
        className
      )}
      role="note"
    >
      <p className="mb-1 font-medium">{title || calloutTitles[type]}</p>
      <div className="text-sm opacity-90">{children}</div>
    </div>
  )
})
